import { supabase } from '@/lib/integrations/supabase'
import {
  createLayoutDefinitionSkeleton,
  executeFileLayout,
  getStoredDocumentType,
} from '@/modules/file-reader'
import {
  getDocumentDownloadUrl,
  deleteDocumentBatch,
  listDocumentBatches,
  uploadDocuments,
  type DocumentUploadProgress,
  type StoredDocument,
} from '@/modules/documents'
import type {
  AccountingProduct,
  EstablishmentActivity,
  FiscalMemoryClearResult,
  FiscalMemoryClearScope,
  FiscalMemoryOperationEvidence,
  FiscalMemoryPartyCode,
  FiscalMemoryProductDetails,
  FiscalMemoryTaxEvidence,
  FiscalMemoryImport,
  SpedItemPurpose,
  SpedMemoryImportResult,
  SpedMemoryPreview,
  SpedMemoryProduct,
  TaxReconciliation,
  TaxReconciliationItem,
  XmlMemoryLinkResult,
  XmlMemoryPreparedItem,
  XmlMemoryPreview,
} from '@/modules/accounting/memory/accountingFiscalMemoryTypes'
import { isCfopCompatibleWithMovement } from '@/modules/accounting/accountingDocumentMovement'

interface AssetAccountingRow {
  asset_id: string
  unit_id: string
  sped_item_type_code: string | null
  item_purpose_key: SpedItemPurpose | null
  ncm: string | null
  cest: string | null
  source_kind: AccountingProduct['source_kind']
  source_period_start: string | null
  source_period_end: string | null
  source_document_name: string | null
  confirmed_at: string | null
  created_at: string
  updated_at: string
  asset: {
    id: string
    name: string
    uom: string
    active: boolean
    created_at: string
    updated_at: string
  }
}

interface AssetCodeRow {
  asset_id: string
  code: string
  is_primary: boolean
}

interface AccountingEvidenceRow {
  asset_id: string
  evidence_kind: 'catalog_classification' | 'historical_operation' | 'manual_confirmation'
  item_purpose_key: SpedItemPurpose | null
  weight: number
  period_end: string | null
}

interface OperationMemoryCountRow {
  asset_id: string
  occurrence_count: number
  period_count: number
}

type MemoryPartyCodeRow = Omit<FiscalMemoryPartyCode, 'party_name'>
interface MemoryOperationRow {
  id: string
  party_id: string | null
  movement_direction: 'incoming' | 'outgoing'
  operation_type_key: string
  item_purpose_key: SpedItemPurpose | null
  source_cfop: string | null
  bookkeeping_cfop: string | null
  operation_nature: string | null
  counterparty_state: string | null
  counterparty_tax_regime: string | null
  occurrence_count: number
  period_count: number
  confidence: number
  first_seen_at: string | null
  last_seen_at: string | null
  attrs: Record<string, unknown>
  created_at: string
}

interface MemoryTaxRow {
  id: string
  operation_memory_id: string
  party_id: string | null
  tax_code: string
  evidence_role: FiscalMemoryTaxEvidence['evidence_role']
  code_system: string | null
  situation_code: string | null
  classification_code: string | null
  nominal_rate: number | null
  base_reduction_rate: number | null
  effective_rate: number | null
  credit_rate: number | null
  has_credit: boolean | null
  occurrence_count: number
  period_count: number
  confidence: number
  first_seen_at: string | null
  last_seen_at: string | null
  source_kind: FiscalMemoryTaxEvidence['source_kind']
  attrs: Record<string, unknown>
  created_at: string
}

interface MemoryPartyRow {
  id: string
  name: string
}

interface MemoryImportRecordRow {
  record_kind: 'participant' | 'document' | 'item'
  source_line: number
  parent_source_line: number | null
  data: Record<string, unknown>
}

function nullableNumber(value: unknown) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const PAGE_SIZE = 1000

const purposeByItemType: Record<string, SpedItemPurpose> = {
  '00': 'resale',
  '01': 'raw_material',
  '02': 'packaging',
  '03': 'work_in_process',
  '04': 'finished_product',
  '05': 'byproduct',
  '06': 'intermediate_product',
  '07': 'use_consumption',
  '08': 'fixed_asset',
  '09': 'service',
  '10': 'other_input',
  '99': 'other',
}

function establishmentActivity(
  sourceKind: 'sped_fiscal' | 'sped_contributions',
  code: string | null,
): EstablishmentActivity | null {
  if (!code) return null
  if (sourceKind === 'sped_fiscal') return code === '0' ? 'industrial' : 'other'
  return ({
    '0': 'industrial',
    '1': 'services',
    '2': 'commerce',
    '3': 'financial',
    '4': 'real_estate',
    '9': 'other',
  } as Record<string, EstablishmentActivity>)[code] || 'other'
}

function stringValue(record: Record<string, unknown>, field: string) {
  const value = record[field]
  return value === undefined || value === null || value === '' ? null : String(value).trim()
}

function numberValue(record: Record<string, unknown>, field: string) {
  const value = record[field]
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const stateByMunicipalityPrefix: Record<string, string> = {
  '11': 'RO', '12': 'AC', '13': 'AM', '14': 'RR', '15': 'PA', '16': 'AP', '17': 'TO',
  '21': 'MA', '22': 'PI', '23': 'CE', '24': 'RN', '25': 'PB', '26': 'PE', '27': 'AL',
  '28': 'SE', '29': 'BA', '31': 'MG', '32': 'ES', '33': 'RJ', '35': 'SP', '41': 'PR',
  '42': 'SC', '43': 'RS', '50': 'MS', '51': 'MT', '52': 'GO', '53': 'DF',
}

function stateFromMunicipalityCode(value: string | null) {
  if (!value) return null
  return stateByMunicipalityPrefix[value.replace(/\D/g, '').slice(0, 2)] || null
}

function taxRegimeFromCrt(value: string | null) {
  if (!value) return null
  if (['1', '2', '4'].includes(value)) return 'simples_nacional'
  if (value === '3') return 'normal'
  return null
}

async function pagedSelect<T>(loadPage: (from: number, to: number) => Promise<{ data: T[] | null; error: any }>) {
  const rows: T[] = []
  let page = 0
  while (true) {
    const { data, error } = await loadPage(page * PAGE_SIZE, ((page + 1) * PAGE_SIZE) - 1)
    if (error) throw error
    const current = data || []
    rows.push(...current)
    if (current.length < PAGE_SIZE) return rows
    page += 1
  }
}

export async function listAccountingProducts(unitId: string): Promise<AccountingProduct[]> {
  const [accountingRows, codeRows, evidenceRows, operationRows] = await Promise.all([
    pagedSelect<AssetAccountingRow>((from, to) => supabase
      .from('asset_accounting')
      .select(`
        asset_id, unit_id, sped_item_type_code, item_purpose_key, ncm, cest,
        source_kind, source_period_start, source_period_end,
        source_document_name, confirmed_at, created_at, updated_at,
        asset:assets!asset_accounting_asset_id_fkey(
          id, name, uom, active, created_at, updated_at
        )
      `)
      .eq('unit_id', unitId)
      .order('updated_at', { ascending: false })
      .range(from, to) as any),
    pagedSelect<AssetCodeRow>((from, to) => supabase
      .from('asset_codes')
      .select('asset_id, code, is_primary')
      .eq('unit_id', unitId)
      .eq('code_type', 'internal')
      .is('party_id', null)
      .order('is_primary', { ascending: false })
      .range(from, to) as any),
    pagedSelect<AccountingEvidenceRow>((from, to) => supabase
      .from('asset_accounting_evidence')
      .select('asset_id, evidence_kind, item_purpose_key, weight, period_end')
      .eq('unit_id', unitId)
      .neq('evidence_kind', 'historical_operation')
      .order('period_end', { ascending: false })
      .range(from, to) as any),
    pagedSelect<OperationMemoryCountRow>((from, to) => supabase
      .from('asset_operation_memory')
      .select('asset_id, occurrence_count, period_count')
      .eq('unit_id', unitId)
      .range(from, to) as any),
  ])

  const codesByAsset = new Map<string, AssetCodeRow[]>()
  codeRows.forEach((code) => codesByAsset.set(code.asset_id, [
    ...(codesByAsset.get(code.asset_id) || []),
    code,
  ]))
  const evidenceByAsset = new Map<string, AccountingEvidenceRow[]>()
  evidenceRows.forEach((evidence) => evidenceByAsset.set(evidence.asset_id, [
    ...(evidenceByAsset.get(evidence.asset_id) || []),
    evidence,
  ]))
  const operationsByAsset = new Map<string, OperationMemoryCountRow[]>()
  operationRows.forEach((operation) => operationsByAsset.set(operation.asset_id, [
    ...(operationsByAsset.get(operation.asset_id) || []),
    operation,
  ]))

  return accountingRows.map((row) => {
    const codes = codesByAsset.get(row.asset_id) || []
    const allEvidence = evidenceByAsset.get(row.asset_id) || []
    const evidence = allEvidence
      .filter((item) => item.evidence_kind !== 'historical_operation')
      .filter((item) => item.item_purpose_key && Number(item.weight) > 0)
    const operationMemory = operationsByAsset.get(row.asset_id) || []
    const operationObservations = operationMemory.reduce(
      (total, operation) => total + Number(operation.occurrence_count || 0),
      0,
    )
    const weights = new Map<SpedItemPurpose, number>()
    evidence.forEach((item) => {
      if (!item.item_purpose_key) return
      weights.set(item.item_purpose_key, (weights.get(item.item_purpose_key) || 0) + Number(item.weight))
    })
    const ranked = Array.from(weights.entries()).sort((left, right) => right[1] - left[1])
    const totalWeight = ranked.reduce((total, [, weight]) => total + weight, 0)
    const dominantPurpose = ranked[0]?.[0] || null
    const confidence = totalWeight > 0 ? Math.round(((ranked[0]?.[1] || 0) / totalWeight) * 100) : 0
    const recentPurposes = evidence.slice(0, 2).map((item) => item.item_purpose_key)
    const recentConflict = recentPurposes.length > 1 && new Set(recentPurposes).size > 1
    const classificationStrong = evidence.length >= 3 && confidence >= 70 && !recentConflict
    const automaticEligible = classificationStrong && operationObservations >= 3
    const classificationStatus = classificationStrong
      ? 'strong' as const
      : ranked.length > 1 || recentConflict
        ? 'review' as const
        : 'insufficient' as const
    const primary = codes.find((code) => code.is_primary)?.code || null
    return {
      ...row,
      name: row.asset.name,
      uom: row.asset.uom,
      active: row.asset.active,
      internal_code: primary,
      previous_codes: codes.filter((code) => !code.is_primary).map((code) => code.code),
      dominant_purpose_key: dominantPurpose,
      classification_observations: evidence.length,
      operation_observations: operationObservations,
      classification_confidence: confidence,
      classification_status: classificationStatus,
      memory_stage: operationMemory.length > 0 ? 'enriched' as const : 'catalog' as const,
      automatic_eligible: automaticEligible,
      created_at: row.asset.created_at,
      updated_at: row.updated_at || row.asset.updated_at,
    }
  })
}

export async function getAccountingProductMemoryDetails(
  unitId: string,
  assetId: string,
): Promise<FiscalMemoryProductDetails> {
  const [partyCodes, operationEvidence, taxEvidence] = await Promise.all([
    pagedSelect<MemoryPartyCodeRow>((from, to) => supabase
      .from('asset_codes')
      .select('id, code, party_id, valid_from, valid_to, source')
      .eq('unit_id', unitId)
      .eq('asset_id', assetId)
      .eq('code_type', 'counterparty')
      .order('updated_at', { ascending: false })
      .range(from, to) as any),
    pagedSelect<MemoryOperationRow>((from, to) => supabase
      .from('asset_operation_memory')
      .select(`
        id, party_id, movement_direction, operation_type_key, item_purpose_key,
        source_cfop, bookkeeping_cfop, operation_nature, counterparty_state,
        counterparty_tax_regime, occurrence_count, period_count, confidence,
        first_seen_at, last_seen_at, attrs, created_at
      `)
      .eq('unit_id', unitId)
      .eq('asset_id', assetId)
      .order('last_seen_at', { ascending: false, nullsFirst: false })
      .range(from, to) as any),
    pagedSelect<MemoryTaxRow>((from, to) => supabase
      .from('asset_operation_tax_memory')
      .select(`
        id, operation_memory_id, party_id, evidence_role, tax_code, code_system,
        situation_code, classification_code, nominal_rate, base_reduction_rate,
        effective_rate, credit_rate, has_credit, occurrence_count, period_count,
        confidence, first_seen_at, last_seen_at, source_kind, attrs, created_at
      `)
      .eq('unit_id', unitId)
      .eq('asset_id', assetId)
      .order('last_seen_at', { ascending: false, nullsFirst: false })
      .range(from, to) as any),
  ])

  const partyIds = Array.from(new Set([
    ...partyCodes.map((row) => row.party_id),
    ...operationEvidence.map((row) => row.party_id),
    ...taxEvidence.map((row) => row.party_id),
  ].filter((partyId): partyId is string => Boolean(partyId))))
  let parties: MemoryPartyRow[] = []
  if (partyIds.length > 0) {
    const { data, error } = await supabase
      .from('parties')
      .select('id, name')
      .in('id', partyIds)
    if (error) throw error
    parties = (data || []) as MemoryPartyRow[]
  }
  const partyNames = new Map(parties.map((party) => [party.id, party.name]))
  const partyName = (partyId: string | null) => partyId ? partyNames.get(partyId) || null : null
  const operationById = new Map(operationEvidence.map((operation) => [operation.id, operation]))

  return {
    party_codes: partyCodes.map((row) => ({ ...row, party_name: partyName(row.party_id) })),
    operation_evidence: operationEvidence.map((row) => ({
      ...row,
      party_name: partyName(row.party_id),
      evidence_kind: 'historical_operation' as const,
      sped_item_type_code: null,
      period_start: row.first_seen_at,
      period_end: row.last_seen_at,
      source_kind: 'learned' as const,
      source_document_name: '',
      attrs: {
        ...row.attrs,
        movement_direction: row.movement_direction,
        xml_cfop: row.source_cfop,
        sped_cfop: row.bookkeeping_cfop,
        operation_nature: row.operation_nature,
      },
    })),
    tax_evidence: taxEvidence.map((row) => ({
      ...row,
      party_name: partyName(row.party_id),
      operation_type_key: null,
      item_purpose_key: null,
      movement_direction: operationById.get(row.operation_memory_id)?.movement_direction || 'incoming',
      base_amount: null,
      nominal_rate: nullableNumber(row.nominal_rate),
      tax_amount: null,
      credit_base_amount: null,
      credit_rate: nullableNumber(row.credit_rate),
      credit_amount: null,
      document_date: row.last_seen_at,
      source_document_name: null,
      source_item_key: row.operation_memory_id,
      attrs: {
        ...row.attrs,
        classification_code: row.classification_code,
      },
    })),
  }
}

export async function listFiscalMemoryImports(unitId: string): Promise<FiscalMemoryImport[]> {
  return pagedSelect<FiscalMemoryImport>((from, to) => supabase
    .from('asset_memory_imports')
    .select('*')
    .eq('unit_id', unitId)
    .order('created_at', { ascending: false })
    .range(from, to) as any)
}

export async function listTaxReconciliations(unitId: string): Promise<TaxReconciliation[]> {
  return pagedSelect<TaxReconciliation>((from, to) => supabase
    .from('tax_reconciliations')
    .select('*')
    .eq('unit_id', unitId)
    .order('period_end', { ascending: false })
    .order('tax_code', { ascending: true })
    .range(from, to) as any)
}

export async function listTaxReconciliationItems(
  unitId: string,
  reconciliationId: string,
): Promise<TaxReconciliationItem[]> {
  return pagedSelect<TaxReconciliationItem>((from, to) => supabase
    .from('tax_reconciliation_items')
    .select('*')
    .eq('unit_id', unitId)
    .eq('reconciliation_id', reconciliationId)
    .order('status', { ascending: true })
    .order('document_key', { ascending: true })
    .range(from, to) as any)
}

export async function clearFiscalMemory(
  unitId: string,
  scope: FiscalMemoryClearScope,
  options?: { importId?: string | null; cutoffDate?: string | null },
): Promise<FiscalMemoryClearResult> {
  const { data, error } = await supabase.rpc('clear_accounting_memory', {
    p_unit_id: unitId,
    p_scope: scope,
    p_import_id: options?.importId || null,
    p_cutoff_date: options?.cutoffDate || null,
  })
  if (error) throw error
  return data as FiscalMemoryClearResult
}

export async function listAvailableSpedDocuments(unitId: string): Promise<StoredDocument[]> {
  const batches = await listDocumentBatches(unitId)
  return batches
    .flatMap((batch) => batch.documents || [])
    .filter((document) => (
      ['sped_fiscal', 'sped_contributions'].includes(document.document_kind)
      && Boolean(document.storage_path)
    ))
    .sort((left, right) => right.created_at.localeCompare(left.created_at))
}

export async function listAvailableXmlDocuments(unitId: string): Promise<StoredDocument[]> {
  const batches = await listDocumentBatches(unitId)
  return batches
    .flatMap((batch) => batch.documents || [])
    .filter((document) => (
      document.document_kind === 'xml'
      && document.status === 'valid'
      && Boolean(document.storage_path)
      && Boolean(document.content_sha256)
    ))
    .sort((left, right) => right.created_at.localeCompare(left.created_at))
}

export async function uploadXmlMemoryDocuments(
  unitId: string,
  files: File[],
  onProgress?: (progress: DocumentUploadProgress) => void,
) {
  const result = await uploadDocuments(unitId, files, onProgress, { purpose: 'fiscal_memory' })
  return {
    result,
    documents: await listAvailableXmlDocuments(unitId),
  }
}

export async function uploadSpedDocument(unitId: string, file: File): Promise<{
  document: StoredDocument
  temporaryBatchId: string
}> {
  const result = await uploadDocuments(unitId, [file], undefined, { purpose: 'fiscal_memory' })
  const documents = await listAvailableSpedDocuments(unitId)
  const uploaded = documents.find((document) => document.batch_id === result.batch.id)
    || documents.find((document) => document.original_name === file.name)
  if (!uploaded) throw new Error(result.duplicates > 0 ? 'accounting_memory_duplicate_not_found' : 'accounting_memory_upload_failed')
  return { document: uploaded, temporaryBatchId: result.batch.id }
}

export async function discardFiscalMemoryUpload(batchId: string): Promise<void> {
  await deleteDocumentBatch(batchId)
}

function profileDiffers(product: Omit<SpedMemoryProduct, 'preview_status'>, current: AccountingProduct) {
  return product.description !== current.name
    || product.inventory_uom !== current.uom
    || product.item_type_code !== current.sped_item_type_code
    || product.ncm !== current.ncm
    || product.cest !== current.cest
}

function candidateCanReplace(
  document: StoredDocument,
  periodEnd: string | null,
  current: AccountingProduct,
) {
  if (current.source_kind === 'manual') return false
  if (!current.source_period_end || !periodEnd) return true
  if (periodEnd > current.source_period_end) return true
  if (periodEnd < current.source_period_end) return false
  return document.document_kind === 'sped_fiscal'
    || current.source_kind !== 'sped_fiscal'
}

export async function prepareSpedMemoryPreview(
  document: StoredDocument,
  currentProducts?: AccountingProduct[],
): Promise<SpedMemoryPreview> {
  if (!document.storage_path || !['sped_fiscal', 'sped_contributions'].includes(document.document_kind)) {
    throw new Error('accounting_memory_sped_required')
  }
  if (!document.content_sha256) throw new Error('accounting_memory_source_hash_required')
  const layoutKey = document.document_kind as 'sped_fiscal' | 'sped_contributions'
  const definition = createLayoutDefinitionSkeleton({ type: layoutKey })
  const signedUrl = await getDocumentDownloadUrl(document.storage_path)
  const response = await fetch(signedUrl)
  if (!response.ok) throw new Error('document_download_failed')
  const execution = executeFileLayout(await response.arrayBuffer(), definition)
  const headerRecord = execution.groups.find((group) => group.key === 'file')?.records[0] || {}
  const taxProfileRecord = execution.groups.find((group) => group.key === 'taxProfile')?.records[0] || {}
  const periodStart = stringValue(headerRecord, 'fiscal.file.period.start')
  const periodEnd = stringValue(headerRecord, 'fiscal.file.period.end')
  const activityCode = stringValue(headerRecord, 'fiscal.establishment.activityIndicator')
  const pisCofinsIncidence = stringValue(taxProfileRecord, 'fiscal.establishment.taxProfile.pisCofinsIncidence')
  const inferredTaxRegime = layoutKey === 'sped_contributions'
    ? pisCofinsIncidence === '1'
      ? 'lucro_real'
      : pisCofinsIncidence === '2'
        ? 'lucro_presumido'
        : pisCofinsIncidence === '3'
          ? 'other'
          : null
    : null
  const existing = currentProducts || await listAccountingProducts(document.unit_id)
  const existingByCode = new Map(existing
    .filter((product) => product.internal_code)
    .map((product) => [product.internal_code!, product]))
  const records = execution.groups.find((group) => group.key === 'products')?.records || []
  const blockCProductCodes = new Set(
    (execution.groups.find((group) => group.key === 'nfeItems')?.records || [])
      .map((record) => stringValue(record, 'fiscal.document.items.productCode'))
      .filter((code): code is string => Boolean(code)),
  )
  const correlationRecords = [
    ...(['participants', 'nfeDocuments', 'nfeItems'] as const).flatMap((groupKey) => {
      const group = execution.groups.find((candidate) => candidate.key === groupKey)
      const recordKind = groupKey === 'participants'
        ? 'participant' as const
        : groupKey === 'nfeDocuments'
          ? 'document' as const
          : 'item' as const
      return (group?.records || []).flatMap((data, index) => {
        const metadata = group?.recordMetadata?.[index]
        if (!metadata?.sourceLine) return []
        return [{
          record_kind: recordKind,
          source_line: metadata.sourceLine,
          parent_source_line: metadata.parentSourceLine,
          data,
        }]
      })
    }),
  ]
  const expectedDocuments = new Set(
    (execution.groups.find((group) => group.key === 'nfeDocuments')?.records || [])
      .map((record) => digits(record['fiscal.document.accessKey']))
      .filter((key) => key.length === 44),
  ).size
  const deduplicated = new Map<string, Omit<SpedMemoryProduct, 'preview_status'>>()
  const catalogCodes = new Set<string>()

  records.forEach((record) => {
    const internalCode = stringValue(record, 'fiscal.product.externalCode')
    const description = stringValue(record, 'fiscal.product.description')
    if (!internalCode || !description) return
    catalogCodes.add(internalCode)
    if (!blockCProductCodes.has(internalCode)) return
    const itemTypeCode = stringValue(record, 'fiscal.product.sourceItemType')?.padStart(2, '0') || null
    deduplicated.set(internalCode, {
      internal_code: internalCode,
      previous_code: stringValue(record, 'fiscal.product.previousCode'),
      description,
      barcode: stringValue(record, 'fiscal.product.barcode'),
      inventory_uom: stringValue(record, 'fiscal.product.inventoryUom') || 'UN',
      item_type_code: itemTypeCode,
      item_purpose_key: itemTypeCode ? purposeByItemType[itemTypeCode] || null : null,
      ncm: stringValue(record, 'fiscal.product.ncm'),
      cest: stringValue(record, 'fiscal.product.cest'),
      ipi_exception_code: stringValue(record, 'fiscal.product.ipiException'),
      genre_code: stringValue(record, 'fiscal.product.genericCode'),
      service_code: stringValue(record, 'fiscal.product.serviceCode'),
      internal_icms_rate: numberValue(record, 'fiscal.product.icmsRate'),
    })
  })

  const products = Array.from(deduplicated.values()).map((product): SpedMemoryProduct => {
    const current = existingByCode.get(product.internal_code)
    const previewStatus = !current
      ? 'new'
      : candidateCanReplace(document, periodEnd, current) && profileDiffers(product, current)
        ? 'update'
        : 'unchanged'
    return { ...product, preview_status: previewStatus }
  })

  return {
    document,
    layoutKey,
    layoutVersion: definition.layoutVersion,
    establishmentName: stringValue(headerRecord, 'fiscal.establishment.name'),
    establishmentTaxId: stringValue(headerRecord, 'fiscal.establishment.taxId'),
    periodStart,
    periodEnd,
    establishmentActivityCode: activityCode,
    establishmentActivityKind: establishmentActivity(layoutKey, activityCode),
    establishmentProfile: {
      legal_name: stringValue(headerRecord, 'fiscal.establishment.name'),
      tax_id: stringValue(headerRecord, 'fiscal.establishment.taxId'),
      state: stringValue(headerRecord, 'fiscal.establishment.state'),
      state_registration: stringValue(headerRecord, 'fiscal.establishment.stateRegistration'),
      municipal_registration: stringValue(headerRecord, 'fiscal.establishment.municipalRegistration'),
      municipality_code: stringValue(headerRecord, 'fiscal.establishment.municipalityCode'),
      suframa: stringValue(headerRecord, 'fiscal.establishment.suframa'),
      fiscal_profile: stringValue(headerRecord, 'fiscal.establishment.fiscalProfile'),
      legal_nature_indicator: stringValue(headerRecord, 'fiscal.establishment.legalNatureIndicator'),
      pis_cofins_incidence: pisCofinsIncidence,
      credit_method: stringValue(taxProfileRecord, 'fiscal.establishment.taxProfile.creditMethod'),
      accounting_method: stringValue(taxProfileRecord, 'fiscal.establishment.taxProfile.accountingMethod'),
      cumulative_regime_indicator: stringValue(taxProfileRecord, 'fiscal.establishment.taxProfile.cumulativeRegimeIndicator'),
      tax_regime: inferredTaxRegime,
      activity_code: activityCode,
      activity_kind: establishmentActivity(layoutKey, activityCode),
    },
    products,
    correlationRecords,
    counts: {
      total: products.length,
      new: products.filter((product) => product.preview_status === 'new').length,
      update: products.filter((product) => product.preview_status === 'update').length,
      unchanged: products.filter((product) => product.preview_status === 'unchanged').length,
      missingItemType: products.filter((product) => !product.item_purpose_key).length,
      expectedDocuments,
      ignoredOutsideBlockC: Array.from(catalogCodes)
        .filter((code) => !blockCProductCodes.has(code)).length,
    },
    warnings: execution.warnings,
  }
}

export async function importSpedMemory(preview: SpedMemoryPreview): Promise<SpedMemoryImportResult> {
  // A new accounting client starts with only a display name. The validated
  // SPED header must establish its tax identity before the import RPC compares
  // the document company with the client unit.
  await updateClientFromSpedPreview(preview)
  const { data, error } = await supabase.rpc('import_sped_0200_memory', {
    p_unit_id: preview.document.unit_id,
    p_document_id: preview.document.id,
    p_layout_key: preview.layoutKey,
    p_layout_version: preview.layoutVersion,
    p_header: {
      period_start: preview.periodStart,
      period_end: preview.periodEnd,
      establishment_name: preview.establishmentName,
      establishment_tax_id: preview.establishmentTaxId,
    },
    p_products: preview.products.map(({ preview_status: _previewStatus, ...product }) => product),
  })
  if (error) throw error
  const { error: recordsError } = await supabase.rpc('store_fiscal_memory_import_records', {
    p_unit_id: preview.document.unit_id,
    p_source_document_hash: preview.document.content_sha256,
    p_records: preview.correlationRecords,
  })
  if (recordsError) throw recordsError
  const { error: activityError } = await supabase
    .from('asset_memory_imports')
    .update({
      establishment_activity_code: preview.establishmentActivityCode,
      establishment_activity_kind: preview.establishmentActivityKind,
      xml_expected_count: preview.counts.expectedDocuments,
      xml_missing_count: preview.counts.expectedDocuments,
    })
    .eq('unit_id', preview.document.unit_id)
    .eq('source_document_hash', preview.document.content_sha256)
  if (activityError) throw activityError
  return data as SpedMemoryImportResult
}

export async function assertFiscalMemoryDocumentsAvailable(
  unitId: string,
  documentIds: string[],
): Promise<void> {
  const expected = Array.from(new Set(documentIds.filter(Boolean)))
  if (expected.length === 0) throw new Error('document_not_found')
  const { data, error } = await supabase
    .from('documents')
    .select('id')
    .eq('unit_id', unitId)
    .in('id', expected)
  if (error) throw error
  if ((data || []).length !== expected.length) throw new Error('document_not_found')
}

async function updateClientFromSpedPreview(preview: SpedMemoryPreview) {
  if (!preview.periodEnd) return
  const { error } = await supabase.rpc('set_accounting_client_sped_profile', {
    p_unit_id: preview.document.unit_id,
    p_profile: preview.establishmentProfile,
    p_period_end: preview.periodEnd,
    p_source_kind: preview.layoutKey,
  })
  if (error) throw error
}

export async function importSpedContributionsMemory(
  preview: SpedMemoryPreview,
): Promise<SpedMemoryImportResult> {
  if (preview.layoutKey !== 'sped_contributions' || !preview.document.content_sha256) {
    throw new Error('accounting_memory_contributions_required')
  }
  await updateClientFromSpedPreview(preview)
  const { error } = await supabase.from('asset_memory_imports').upsert({
    unit_id: preview.document.unit_id,
    source_document_id: preview.document.id,
    source_document_hash: preview.document.content_sha256,
    source_document_name: preview.document.original_name,
    source_kind: preview.layoutKey,
    source_layout_key: preview.layoutKey,
    source_layout_version: preview.layoutVersion,
    establishment_name: preview.establishmentName,
    establishment_tax_id: preview.establishmentTaxId,
    establishment_activity_code: preview.establishmentActivityCode,
    establishment_activity_kind: preview.establishmentActivityKind,
    period_start: preview.periodStart,
    period_end: preview.periodEnd,
    status: preview.warnings.length > 0 ? 'completed_with_warnings' : 'completed',
    product_count: 0,
    created_count: 0,
    updated_count: 0,
    unchanged_count: 0,
    missing_item_type_count: 0,
    xml_expected_count: preview.counts.expectedDocuments,
    xml_missing_count: preview.counts.expectedDocuments,
    completed_at: new Date().toISOString(),
  }, { onConflict: 'unit_id,source_document_hash' })
  if (error) throw error

  const { error: recordsError } = await supabase.rpc('store_fiscal_memory_import_records', {
    p_unit_id: preview.document.unit_id,
    p_source_document_hash: preview.document.content_sha256,
    p_records: preview.correlationRecords,
  })
  if (recordsError) throw recordsError
  return {
    product_count: 0,
    created_count: 0,
    updated_count: 0,
    unchanged_count: 0,
    missing_item_type_count: 0,
  }
}

function digits(value: unknown) {
  return String(value ?? '').replace(/\D/g, '')
}

function inPeriod(value: string | null, start: string | null, end: string | null) {
  if (!value || !start || !end) return true
  const date = value.slice(0, 10)
  return date >= start && date <= end
}

function documentSummary(document: StoredDocument) {
  const summary = document.metadata?.fiscal_summary || document.metadata?.summary
  return summary && typeof summary === 'object' ? summary as Record<string, unknown> : {}
}

function taxEvidence(
  source: Record<string, unknown>,
  prefix: string,
  role: 'document_emission' | 'prior_bookkeeping',
  sourceKind: 'xml_document' | 'sped_fiscal' | 'sped_contributions',
  layoutKey: string,
  layoutVersion: string,
  incoming: boolean,
  allowedTaxCodes: string[] = ['icms', 'ipi', 'pis', 'cofins', 'cbs', 'ibs', 'is'],
) {
  return allowedTaxCodes.flatMap((taxCode) => {
    const situation = stringValue(source, `${prefix}.taxes.${taxCode}.situation`)
    const base = numberValue(source, `${prefix}.taxes.${taxCode}.base`)
    const rate = numberValue(source, `${prefix}.taxes.${taxCode}.rate`)
    const amount = numberValue(source, `${prefix}.taxes.${taxCode}.amount`)
    const operationTotal = numberValue(source, `${prefix}.total`)
    const baseReduction = base !== null && operationTotal !== null && operationTotal > 0
      ? Math.min(100, Math.max(0, ((operationTotal - base) / operationTotal) * 100))
      : null
    const effectiveRate = base !== null && base > 0 && amount !== null
      ? (amount / base) * 100
      : rate
    const classificationCode = stringValue(source, `${prefix}.taxes.${taxCode}.classificationCode`)
    const stateRate = numberValue(source, `${prefix}.taxes.${taxCode}.stateRate`)
    const stateAmount = numberValue(source, `${prefix}.taxes.${taxCode}.stateAmount`)
    const municipalRate = numberValue(source, `${prefix}.taxes.${taxCode}.municipalRate`)
    const municipalAmount = numberValue(source, `${prefix}.taxes.${taxCode}.municipalAmount`)
    if (situation === null && base === null && rate === null && amount === null
      && classificationCode === null && stateRate === null && stateAmount === null
      && municipalRate === null && municipalAmount === null) return []
    return [{
      evidence_role: role,
      tax_code: taxCode,
      code_system: situation
        ? taxCode === 'icms'
          ? 'CST_ICMS_CSOSN'
          : ['ibs', 'cbs'].includes(taxCode)
            ? 'CST_IBS_CBS'
            : taxCode === 'is'
              ? 'CST_IS'
              : `CST_${taxCode.toUpperCase()}`
        : null,
      situation_code: situation,
      base_amount: base,
      base_reduction_rate: baseReduction,
      nominal_rate: rate,
      effective_rate: effectiveRate,
      tax_amount: amount,
      credit_base_amount: role === 'prior_bookkeeping' && incoming ? base : null,
      credit_rate: role === 'prior_bookkeeping' && incoming ? rate : null,
      credit_amount: role === 'prior_bookkeeping' && incoming ? amount : null,
      source_kind: sourceKind,
      source_layout_key: layoutKey,
      source_layout_version: layoutVersion,
      attrs: {
        classification_code: classificationCode,
        ibs_state_rate: stateRate,
        ibs_state_amount: stateAmount,
        ibs_municipal_rate: municipalRate,
        ibs_municipal_amount: municipalAmount,
        evidence_nature: role === 'document_emission' ? 'issuer_reported' : 'prior_bookkeeping',
        provisional: role === 'document_emission' && ['cbs', 'ibs', 'is'].includes(taxCode),
        officially_reconciled: false,
      },
    }]
  })
}

async function downloadExecution(document: StoredDocument, type: 'nfe' | 'sped_fiscal' | 'sped_contributions') {
  if (!document.storage_path) throw new Error('accounting_memory_source_unavailable')
  const response = await fetch(await getDocumentDownloadUrl(document.storage_path))
  if (!response.ok) throw new Error('document_download_failed')
  const definition = createLayoutDefinitionSkeleton({ type })
  return {
    definition,
    execution: executeFileLayout(await response.arrayBuffer(), definition),
  }
}

export function xmlDocumentsForPeriod(
  documents: StoredDocument[],
  memoryImport: FiscalMemoryImport,
) {
  return documents.filter((document) => {
    if (getStoredDocumentType(document) !== 'nfe') return false
    const issuedAt = stringValue(documentSummary(document), 'issued_at')
    if (!issuedAt || !memoryImport.period_start || !memoryImport.period_end) return false
    return inPeriod(issuedAt, memoryImport.period_start, memoryImport.period_end)
  })
}

async function listMemoryImportRecords(memoryImport: FiscalMemoryImport) {
  return pagedSelect<MemoryImportRecordRow>((from, to) => supabase
    .from('asset_memory_import_records')
    .select('record_kind, source_line, parent_source_line, data')
    .eq('unit_id', memoryImport.unit_id)
    .eq('memory_import_id', memoryImport.id)
    .order('source_line', { ascending: true })
    .range(from, to) as any)
}

export async function discardMemoryImportRecords(memoryImport: FiscalMemoryImport): Promise<void> {
  const { error } = await supabase
    .from('asset_memory_import_records')
    .delete()
    .eq('unit_id', memoryImport.unit_id)
    .eq('memory_import_id', memoryImport.id)
  if (error) throw error
}

export async function prepareXmlMemoryPreview(
  memoryImport: FiscalMemoryImport,
  spedDocument: StoredDocument | null,
  documents: StoredDocument[],
  currentProducts: AccountingProduct[],
): Promise<XmlMemoryPreview> {
  const spedType = memoryImport.source_kind
  const spedDefinition = createLayoutDefinitionSkeleton({ type: spedType })
  let storedRecords: MemoryImportRecordRow[] = []
  try {
    storedRecords = await listMemoryImportRecords(memoryImport)
  } catch (recordsError) {
    console.warn('[FiscalMemory] Structured SPED records unavailable; trying source file.', recordsError)
  }
  if (storedRecords.length === 0) {
    if (!spedDocument || spedDocument.id !== memoryImport.source_document_id) {
      throw new Error('accounting_memory_sped_source_required')
    }
    const { execution } = await downloadExecution(spedDocument, spedType)
    storedRecords = (['participants', 'nfeDocuments', 'nfeItems'] as const).flatMap((groupKey) => {
      const group = execution.groups.find((candidate) => candidate.key === groupKey)
      const recordKind = groupKey === 'participants'
        ? 'participant' as const
        : groupKey === 'nfeDocuments'
          ? 'document' as const
          : 'item' as const
      return (group?.records || []).flatMap((data, index) => {
        const metadata = group?.recordMetadata?.[index]
        return metadata?.sourceLine ? [{
          record_kind: recordKind,
          source_line: metadata.sourceLine,
          parent_source_line: metadata.parentSourceLine,
          data,
        }] : []
      })
    })
  }
  const participantRecords = storedRecords.filter((record) => record.record_kind === 'participant')
  const documentRecords = storedRecords.filter((record) => record.record_kind === 'document')
  const itemRecords = storedRecords.filter((record) => record.record_kind === 'item')
  if (documentRecords.length === 0 || itemRecords.length === 0) {
    throw new Error('accounting_memory_sped_documents_missing')
  }
  const activityCode = memoryImport.establishment_activity_code
  const activityKind = memoryImport.establishment_activity_kind

  const participants = new Map(participantRecords.map(({ data }) => [
    stringValue(data, 'fiscal.participant.externalCode') || '',
    {
      name: stringValue(data, 'fiscal.participant.name'),
      taxId: stringValue(data, 'fiscal.participant.taxId'),
      state: stateFromMunicipalityCode(stringValue(data, 'fiscal.participant.municipalityCode')),
    },
  ]))
  const products = new Map(currentProducts
    .filter((product) => product.internal_code)
    .map((product) => [product.internal_code!, product]))
  const spedDocuments = new Map<number, Record<string, unknown>>()
  documentRecords.forEach((record) => spedDocuments.set(record.source_line, record.data))
  const spedItemsByDocument = new Map<number, Array<Record<string, unknown>>>()
  itemRecords.forEach((record) => {
    const parentLine = record.parent_source_line
    if (!parentLine) return
    spedItemsByDocument.set(parentLine, [...(spedItemsByDocument.get(parentLine) || []), record.data])
  })
  const spedByAccessKey = new Map<string, {
    record: Record<string, unknown>
    items: Array<Record<string, unknown>>
  }>()
  spedDocuments.forEach((record, sourceLine) => {
    const key = digits(record['fiscal.document.accessKey'])
    const status = stringValue(record, 'fiscal.document.statusCode')
    if (key.length === 44 && (!status || ['00', '01'].includes(status))) {
      spedByAccessKey.set(key, { record, items: spedItemsByDocument.get(sourceLine) || [] })
    }
  })

  const candidates = documents.filter((document) => getStoredDocumentType(document) === 'nfe')
  const outsidePeriod = candidates.filter((document) => {
    const issuedAt = stringValue(documentSummary(document), 'issued_at')
    return !inPeriod(issuedAt, memoryImport.period_start, memoryImport.period_end)
  }).length
  const selectedDocuments = candidates.filter((document) => {
    const issuedAt = stringValue(documentSummary(document), 'issued_at')
    return inPeriod(issuedAt, memoryImport.period_start, memoryImport.period_end)
  })
  const prepared = await Promise.all(selectedDocuments.map(async (document) => {
    if (!document.content_sha256) return { document, reason: 'missing_hash', items: [] as XmlMemoryPreparedItem[] }
    try {
      const { definition, execution } = await downloadExecution(document, 'nfe')
      const header = execution.groups.find((group) => group.key === 'document')?.records[0] || {}
      const xmlItems = execution.groups.find((group) => group.key === 'items')?.records || []
      const accessKey = digits(header['fiscal.document.accessKey'] || document.external_key)
      const spedDocumentMatch = spedByAccessKey.get(accessKey)
      if (!spedDocumentMatch) return { document, reason: 'not_in_sped', items: [] as XmlMemoryPreparedItem[] }
      if (xmlItems.length !== spedDocumentMatch.items.length) {
        return { document, reason: 'item_count_mismatch', items: [] as XmlMemoryPreparedItem[] }
      }
      const operationIndicator = stringValue(spedDocumentMatch.record, 'fiscal.document.operationIndicator')
      const direction = operationIndicator === '1' ? 'outgoing' as const : 'incoming' as const
      const party = participants.get(stringValue(spedDocumentMatch.record, 'fiscal.document.partyCode') || '')
      const documentDate = stringValue(spedDocumentMatch.record, 'fiscal.document.issuedAt')
        || stringValue(header, 'fiscal.document.issuedAt')
      if (!inPeriod(documentDate, memoryImport.period_start, memoryImport.period_end)) {
        return { document, reason: 'outside_period', items: [] as XmlMemoryPreparedItem[] }
      }
      const mapped: XmlMemoryPreparedItem[] = []
      for (let index = 0; index < xmlItems.length; index += 1) {
        const xmlItem = xmlItems[index]
        const lineNumber = numberValue(xmlItem, 'fiscal.document.items.lineNumber') || index + 1
        const spedItem = spedDocumentMatch.items.find((candidate) => (
          numberValue(candidate, 'fiscal.document.items.lineNumber') === lineNumber
        )) || spedDocumentMatch.items[index]
        const internalCode = stringValue(spedItem, 'fiscal.document.items.productCode')
        const product = internalCode ? products.get(internalCode) : null
        if (!internalCode || !product) return { document, reason: 'product_not_in_memory', items: [] as XmlMemoryPreparedItem[] }
        const sourceItemKey = `${accessKey}:${lineNumber}`
        const spedCfop = stringValue(spedItem, 'fiscal.document.items.cfop')
        const bookkeepingCfop = isCfopCompatibleWithMovement(spedCfop, direction) ? spedCfop : null
        mapped.push({
          internal_code: internalCode,
          counterparty_item_code: stringValue(xmlItem, 'fiscal.document.items.productCode'),
          counterparty_name: party?.name || null,
          counterparty_tax_id: party?.taxId || stringValue(header, direction === 'incoming' ? 'fiscal.document.issuer.taxId' : 'fiscal.document.recipient.taxId'),
          counterparty_state: direction === 'incoming'
            ? stringValue(header, 'fiscal.document.issuer.state') || party?.state || null
            : stringValue(header, 'fiscal.document.recipient.state') || party?.state || null,
          counterparty_tax_regime: direction === 'incoming'
            ? taxRegimeFromCrt(stringValue(header, 'fiscal.document.issuer.taxRegime'))
            : null,
          movement_direction: direction,
          operation_type_key: direction === 'incoming' ? 'incoming_purchase' : 'outgoing_sale',
          item_purpose_key: product.item_purpose_key,
          sped_item_type_code: product.sped_item_type_code,
          access_key: accessKey,
          source_item_key: sourceItemKey,
          document_date: documentDate,
          description: stringValue(xmlItem, 'fiscal.document.items.description') || product.name,
          quantity: numberValue(xmlItem, 'fiscal.document.items.quantity'),
          uom: stringValue(xmlItem, 'fiscal.document.items.uom'),
          ncm: stringValue(xmlItem, 'fiscal.document.items.ncm') || product.ncm,
          cfop: bookkeepingCfop,
          source_cfop: stringValue(xmlItem, 'fiscal.document.items.cfop'),
          bookkeeping_cfop: bookkeepingCfop,
          xml_document_id: document.id,
          xml_document_hash: document.content_sha256,
          xml_document_name: document.original_name,
          xml_layout_key: definition.key,
          xml_layout_version: definition.layoutVersion,
          tax_evidence: [
            ...taxEvidence(xmlItem, 'fiscal.document.items', 'document_emission', 'xml_document', definition.key, definition.layoutVersion, direction === 'incoming'),
            ...taxEvidence(
              spedItem,
              'fiscal.document.items',
              'prior_bookkeeping',
              spedType,
              spedDefinition.key,
              spedDefinition.layoutVersion,
              direction === 'incoming',
              spedType === 'sped_fiscal' ? ['icms', 'ipi'] : ['pis', 'cofins'],
            ),
          ],
          attrs: {
            operation_nature: stringValue(header, 'fiscal.document.operationNature'),
            document_number: stringValue(header, 'fiscal.document.number'),
            quantity: numberValue(xmlItem, 'fiscal.document.items.quantity'),
            xml_cfop: stringValue(xmlItem, 'fiscal.document.items.cfop'),
            sped_cfop: bookkeepingCfop,
          },
        })
      }
      return { document, reason: '', items: mapped }
    } catch (error) {
      console.error('[FiscalMemory] XML read error:', document.original_name, error)
      return { document, reason: 'read_error', items: [] as XmlMemoryPreparedItem[] }
    }
  }))

  return {
    memoryImport: {
      ...memoryImport,
      establishment_activity_code: activityCode,
      establishment_activity_kind: activityKind,
    },
    selectedDocuments,
    items: prepared.flatMap((entry) => entry.items),
    matchedDocuments: prepared.filter((entry) => !entry.reason).length,
    expectedDocuments: spedByAccessKey.size,
    unmatchedDocuments: prepared
      .filter((entry) => entry.reason)
      .map((entry) => ({ document: entry.document, reason: entry.reason })),
    ignoredOutsidePeriod: outsidePeriod,
  }
}

export async function linkXmlMemory(preview: XmlMemoryPreview): Promise<XmlMemoryLinkResult> {
  const unmatchedById = new Map(preview.unmatchedDocuments.map((entry) => [entry.document.id, entry.reason]))
  const { data, error } = await supabase.rpc('link_xml_fiscal_memory', {
    p_unit_id: preview.memoryImport.unit_id,
    p_import_id: preview.memoryImport.id,
    p_items: preview.items,
    p_summary: {
      document_count: preview.selectedDocuments.length,
      matched_documents: preview.matchedDocuments,
      expected_documents: preview.expectedDocuments,
      unmatched_documents: preview.unmatchedDocuments.length,
      ignored_outside_period: preview.ignoredOutsidePeriod,
      establishment_activity_code: preview.memoryImport.establishment_activity_code,
      establishment_activity_kind: preview.memoryImport.establishment_activity_kind,
      documents: preview.selectedDocuments
        .filter((document) => document.content_sha256)
        .map((document) => {
          const reason = unmatchedById.get(document.id) || null
          return {
            document_id: document.id,
            hash: document.content_sha256,
            name: document.original_name,
            access_key: document.external_key,
            status: reason ? 'unmatched' : 'matched',
            reason,
            item_count: preview.items.filter((item) => item.xml_document_id === document.id).length,
          }
        }),
    },
  })
  if (error) throw error
  return data as XmlMemoryLinkResult
}
